<template>
  <div class="plan-details">
    <p v-if="planHeader.length" class="plan-details__detail-header">
      {{ planHeader }}
    </p>
    <span v-for="detail in planDetails" :key="`detail-${detail.info}`">
      <p v-if="detail.show" class="plan-details__detail">
        <span v-if="detail.show" class="plan-details__detail-wrapper">
          <CheckmarkIcon v-if="detail.checked" class="plan-details__icon" />
          <XIcon v-else class="plan-details__icon plan-details__icon--x" />
          <span
            class="plan-details__detail-info"
            :class="{ 'plan-details__detail-info--checked': detail.checked }"
            >{{ detail.info }}</span
          >
        </span>
      </p>
    </span>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CheckmarkIcon from '@/assets/icons/greencheck_v2.svg'
import XIcon from '@/assets/icons/x_v2.svg'
import CONSTANTS from '@/constants'

export default {
  name: 'PlanListItemDetails',
  components: {
    CheckmarkIcon: { ...CheckmarkIcon, name: 'CheckMarkIcon' },
    XIcon: { ...XIcon, name: 'XIcon' },
  },
  props: {
    plan: {
      type: Object,
      required: true,
    },
  },
  methods: {
    HandsOnLabsFreeTrial() {
      const { PLANS } = CONSTANTS
      let featureText = ''
      switch (this.plan.merchant_id) {
        case PLANS.PREMIUM:
          featureText =
            'Access to select hands-on labs during trial period, unlimited access to all Premium labs after trial period ends, upon purchase of Premium'
          break
        case PLANS.MONTHLY:
          featureText =
            'Access to select hands-on labs during trial period, unlimited access to all Fundamentals labs upon purchase of Fundamentals Monthly'
          break
        case PLANS.ANNUAL:
          featureText =
            'Access to select hands-on labs during trial period, unlimited access to all Fundamentals labs upon purchase of Fundamentals Annual'
          break
        default:
          break
      }
      return featureText
    },
  },
  computed: {
    ...mapState({
      planCodes: state => state.products.planCodes,
    }),
    isFreeTrialRoute() {
      return this.$route.name === 'free-trial'
    },
    isAnnualPlan() {
      return this.plan.merchant_id === CONSTANTS.PLANS.ANNUAL
    },
    planHeader() {
      return ''
    },
    planDetails() {
      const {
        PLANS: {
          ANNUAL,
          BAH,
          BLKFRIDAY,
          LEGACY,
          MONTHLY,
          MONTHLY_EJPTV2,
          QUARTERLY_EJPTV2,
          PREMIUM2YR,
          PREMIUM,
          PREMIUM_MONTHLY,
          EDA_PREMIUM,
          PTA,
        },
      } = CONSTANTS
      return [
        {
          show: [MONTHLY, ANNUAL].includes(this.plan.merchant_id),
          checked: true,
          info: "Access to INE's Introductory Learning Paths including Pentester Student, CCNA, and Cloud Foundations.",
        },
        {
          show: [MONTHLY, ANNUAL].includes(this.plan.merchant_id),
          checked: true,
          info: 'Github and Azure projects for intro courses',
        },
        {
          show: [PREMIUM, PREMIUM_MONTHLY, PTA, BAH, EDA_PREMIUM].includes(this.plan.merchant_id),
          checked: true,
          info: 'Access entire course library',
        },
        {
          show: [PREMIUM, PREMIUM_MONTHLY, PTA, BAH, EDA_PREMIUM].includes(this.plan.merchant_id),
          checked: true,
          info: 'Github and Azure projects',
        },
        {
          show: [MONTHLY, ANNUAL, PREMIUM, PREMIUM_MONTHLY, PTA, BAH, EDA_PREMIUM].includes(
            this.plan.merchant_id
          ),
          checked: true,
          info: this.isFreeTrialRoute ? this.HandsOnLabsFreeTrial() : 'Hands-on Labs',
        },
        {
          show: [PREMIUM, PREMIUM_MONTHLY, PTA, BAH, EDA_PREMIUM].includes(this.plan.merchant_id),
          checked: true,
          info: 'Live Online Bootcamps',
        },
        {
          show: [PREMIUM, PREMIUM_MONTHLY, PTA, BAH, EDA_PREMIUM].includes(this.plan.merchant_id),
          checked: true,
          info: 'Networking Workbooks',
        },
        {
          show: [BAH].includes(this.plan.merchant_id),
          checked: true,
          info: '25% off an eLearnSecurity certification voucher',
        },
        {
          show: [PREMIUM, PTA].includes(this.plan.merchant_id) && !this.isFreeTrialRoute,
          checked: true,
          info: '50% off an eLearnSecurity certification voucher',
        },
        {
          show: [EDA_PREMIUM].includes(this.plan.merchant_id),
          checked: true,
          info: '50% off all new certifications',
        },
        {
          show: [PREMIUM].includes(this.plan.merchant_id) && this.isFreeTrialRoute,
          checked: true,
          info: `50% off an eLearnSecurity certification voucher 
            after trial period ends, upon purchase of Premium`,
        },
        {
          show: [PREMIUM, PTA, BAH].includes(this.plan.merchant_id) && !this.isFreeTrialRoute,
          checked: [PTA, BAH].includes(this.plan.merchant_id),
          info: 'Access to PentesterAcademy ($249 value)',
        },
        {
          show: [MONTHLY, ANNUAL].includes(this.plan.merchant_id),
          checked: this.isAnnualPlan,
          info: this.isFreeTrialRoute
            ? `Free eJPT and ICCA voucher after trial period ends${
                this.isAnnualPlan ? ', upon purchase of Fundamentals Annual' : ''
              }`
            : 'Free eJPT and ICCA Voucher',
        },
      ]
    },
  },
}
</script>

<style lang="scss">
.plan-details {
  &__detail {
    margin: 10px 0;
    display: flex;
    align-items: flex-start;
    font-family: var(--font-regular);
    color: var(--gray-8);
    width: 100%;
    white-space: wrap;
    font-size: rem(14px);
    line-height: 20px;
    @include mobile {
      margin: 5px 0;
    }
    &-info {
      text-align: left;
      &--checked {
        color: var(--white);
      }
    }
    &-header {
      margin: 10px 0;
      display: flex;
      align-items: center;
      font-family: var(--font-regular);
      width: 100%;
      white-space: nowrap;
      font-size: 16px;
      color: var(--white);
    }
    &-wrapper {
      width: 100%;
      display: grid;
      grid-template-columns: 23px 90%;
      align-items: baseline;
    }
  }
  &__icon {
    width: 12px;
    max-width: 15px;
    margin-right: 10px;
    &--x {
      position: relative;
      top: 3px;
      right: 3px;
      width: 15px;
    }
  }
}
</style>
